import { create } from 'zustand';

// Persisted user gameplay/UI preferences.
export interface SettingsState {
  showDamageNumbers: boolean;
  showNameplates: boolean;
  showFps: boolean;
  sfxVolume: number; // 0..1
  sfxMuted: boolean;

  // Automation preferences (used by FieldAutomation/FieldScene).
  autoUseHpPotion: boolean;
  autoUseMpPotion: boolean;
  hpPotionThreshold: number; // 0..1, use HP potion when hp/maxHp < threshold
  mpPotionThreshold: number; // 0..1, use MP potion when mp/maxMp < threshold
  autoCastSkill: boolean;    // cast skills off-cooldown during auto-attack
  autoCastSkillIds: number[]; // whitelist of skill IDs to auto-cast (empty = all learned)
  autoPickupZen: boolean;    // auto-pickup Zen piles in PICKUP_RANGE
  autoPickupEquip: boolean;  // auto-pickup equipment piles in PICKUP_RANGE
  autoWalkToLoot: boolean;   // walk toward distant loot during auto-attack
  adminKeepSpacing: boolean; // [admin] kite the target to keep attack-range spacing

  setShowDamageNumbers: (v: boolean) => void;
  setShowNameplates: (v: boolean) => void;
  setShowFps: (v: boolean) => void;
  setSfxVolume: (v: number) => void;
  setSfxMuted: (v: boolean) => void;

  setAutoUseHpPotion: (v: boolean) => void;
  setAutoUseMpPotion: (v: boolean) => void;
  setHpPotionThreshold: (v: number) => void;
  setMpPotionThreshold: (v: number) => void;
  setAutoCastSkill: (v: boolean) => void;
  setAutoCastSkillIds: (ids: number[]) => void;
  toggleAutoCastSkillId: (id: number) => void;
  setAutoPickupZen: (v: boolean) => void;
  setAutoPickupEquip: (v: boolean) => void;
  setAutoWalkToLoot: (v: boolean) => void;
  setAdminKeepSpacing: (v: boolean) => void;
}

const STORAGE_KEY = 'hero-settings-v1';

interface Persisted {
  showDamageNumbers?: boolean;
  showNameplates?: boolean;
  showFps?: boolean;
  sfxVolume?: number;
  sfxMuted?: boolean;
  autoUseHpPotion?: boolean;
  autoUseMpPotion?: boolean;
  hpPotionThreshold?: number;
  mpPotionThreshold?: number;
  autoCastSkill?: boolean;
  autoCastSkillIds?: number[];
  autoPickupZen?: boolean;
  autoPickupEquip?: boolean;
  autoWalkToLoot?: boolean;
  adminKeepSpacing?: boolean;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Persisted;
  } catch {
    return {};
  }
}

function persist(s: Persisted) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

const init = load();

function clamp01(v: number, fallback: number): number {
  if (typeof v !== 'number' || Number.isNaN(v)) return fallback;
  return Math.max(0, Math.min(1, v));
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  showDamageNumbers: init.showDamageNumbers ?? true,
  showNameplates: init.showNameplates ?? true,
  showFps: init.showFps ?? true,
  sfxVolume: clamp01(init.sfxVolume as number, 0.7),
  sfxMuted: init.sfxMuted ?? false,

  autoUseHpPotion: init.autoUseHpPotion ?? true,
  autoUseMpPotion: init.autoUseMpPotion ?? true,
  hpPotionThreshold: clamp01(init.hpPotionThreshold as number, 0.5),
  mpPotionThreshold: clamp01(init.mpPotionThreshold as number, 0.3),
  autoCastSkill: init.autoCastSkill ?? true,
  autoCastSkillIds: Array.isArray(init.autoCastSkillIds)
    ? init.autoCastSkillIds.filter((x) => typeof x === 'number')
    : [],
  autoPickupZen: init.autoPickupZen ?? true,
  autoPickupEquip: init.autoPickupEquip ?? true,
  autoWalkToLoot: init.autoWalkToLoot ?? true,
  adminKeepSpacing: init.adminKeepSpacing ?? false,

  setShowDamageNumbers: (v) => { set({ showDamageNumbers: v }); persist({ ...get(), showDamageNumbers: v }); },
  setShowNameplates: (v) => { set({ showNameplates: v }); persist({ ...get(), showNameplates: v }); },
  setShowFps: (v) => { set({ showFps: v }); persist({ ...get(), showFps: v }); },
  setSfxVolume: (v) => {
    const clamped = clamp01(v, 0);
    set({ sfxVolume: clamped });
    persist({ ...get(), sfxVolume: clamped });
  },
  setSfxMuted: (v) => { set({ sfxMuted: v }); persist({ ...get(), sfxMuted: v }); },

  setAutoUseHpPotion: (v) => { set({ autoUseHpPotion: v }); persist({ ...get(), autoUseHpPotion: v }); },
  setAutoUseMpPotion: (v) => { set({ autoUseMpPotion: v }); persist({ ...get(), autoUseMpPotion: v }); },
  setHpPotionThreshold: (v) => {
    const clamped = clamp01(v, 0.5);
    set({ hpPotionThreshold: clamped });
    persist({ ...get(), hpPotionThreshold: clamped });
  },
  setMpPotionThreshold: (v) => {
    const clamped = clamp01(v, 0.3);
    set({ mpPotionThreshold: clamped });
    persist({ ...get(), mpPotionThreshold: clamped });
  },
  setAutoCastSkill: (v) => { set({ autoCastSkill: v }); persist({ ...get(), autoCastSkill: v }); },
  setAutoCastSkillIds: (ids) => {
    const uniq = Array.from(new Set(ids.filter((x) => typeof x === 'number')));
    set({ autoCastSkillIds: uniq });
    persist({ ...get(), autoCastSkillIds: uniq });
  },
  toggleAutoCastSkillId: (id) => {
    const cur = get().autoCastSkillIds;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    set({ autoCastSkillIds: next });
    persist({ ...get(), autoCastSkillIds: next });
  },
  setAutoPickupZen: (v) => { set({ autoPickupZen: v }); persist({ ...get(), autoPickupZen: v }); },
  setAutoPickupEquip: (v) => { set({ autoPickupEquip: v }); persist({ ...get(), autoPickupEquip: v }); },
  setAutoWalkToLoot: (v) => { set({ autoWalkToLoot: v }); persist({ ...get(), autoWalkToLoot: v }); },
  setAdminKeepSpacing: (v) => { set({ adminKeepSpacing: v }); persist({ ...get(), adminKeepSpacing: v }); },
}));

// Non-React accessor for use inside Phaser scenes / entities.
export function getSettingsSnapshot() {
  return useSettingsStore.getState();
}
